import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export interface OnboardEmployeePayload {
  username: string;
  password: string;
  roleId: number | null;
  status: number;
  empCode?: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  emailAddress: string;
  contact: string;
  alternateContact: string | null;
  dateOfJoining: string;
}

export interface OnboardedEmployee {
  userId: number;
  employeeId: number;
  empCode: string;
}

export interface EmployeeProfile {
  id: number;
  emp_code: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email_address: string;
  contact: string;
  alternate_contact: string | null;
  date_of_joining: string;
  date_of_resignation: string | null;
  last_working_day: string | null;
  status: string;
  user_id: number;
  username: string;
  role: string | null;
}

export type DocumentStatus = "PENDING" | "VERIFIED" | "REJECTED";

export interface DocumentItem {
  documentTypeId: number;
  documentName: string;
  isMandatory: boolean;
  documentId: number | null;
  fileName: string | null;
  fileSize: number | null;
  mimeType: string | null;
  documentNumber: string | null;
  status: DocumentStatus | null;
  remarks: string | null;
  uploadedAt: string | null;
  verifiedAt: string | null;
  verifiedByName: string | null;
}

export interface DocumentCategory {
  categoryId: number;
  categoryName: string;
  documents: DocumentItem[];
}

interface OnboardingState {
  submitting: boolean;
  lastOnboarded: OnboardedEmployee | null;
  employee: EmployeeProfile | null;
  employeeLoading: boolean;
  categories: DocumentCategory[];
  checklistLoading: boolean;
  uploadingTypeId: number | null;
  reviewingDocumentId: number | null;
}

const initialState: OnboardingState = {
  submitting: false,
  lastOnboarded: null,
  employee: null,
  employeeLoading: false,
  categories: [],
  checklistLoading: false,
  uploadingTypeId: null,
  reviewingDocumentId: null,
};

export const onboardEmployee = createAsyncThunk(
  "onboarding/onboardEmployee",
  async (payload: OnboardEmployeePayload, { rejectWithValue }) => {
    const res = await fetch("/api/admin/onboarding/employees", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to onboard employee.");
    return data as OnboardedEmployee;
  }
);

export interface LinkExistingEmployeePayload {
  userId: number;
  empCode?: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  emailAddress: string;
  contact: string;
  alternateContact: string | null;
  dateOfJoining: string;
}

export const linkExistingEmployee = createAsyncThunk(
  "onboarding/linkExistingEmployee",
  async (payload: LinkExistingEmployeePayload, { rejectWithValue }) => {
    const res = await fetch("/api/admin/onboarding/link-employee", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to create employee profile.");
    return data as { employeeId: number; empCode: string };
  }
);

export const fetchOnboardingEmployee = createAsyncThunk(
  "onboarding/fetchEmployee",
  async (employeeId: string, { rejectWithValue }) => {
    const res = await fetch(`/api/admin/onboarding/employees/${employeeId}`);
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to load employee.");
    return data.employee as EmployeeProfile;
  }
);

export const fetchDocumentChecklist = createAsyncThunk(
  "onboarding/fetchChecklist",
  async (employeeId: string, { rejectWithValue }) => {
    const res = await fetch(`/api/admin/employee-documents?employeeId=${employeeId}`);
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to load documents.");
    return data.categories as DocumentCategory[];
  }
);

export const uploadEmployeeDocument = createAsyncThunk(
  "onboarding/uploadDocument",
  async (
    args: {
      employeeId: string;
      documentTypeId: number;
      documentNumber?: string;
      file: File;
    },
    { dispatch, rejectWithValue }
  ) => {
    const formData = new FormData();
    formData.append("employeeId", args.employeeId);
    formData.append("documentTypeId", String(args.documentTypeId));
    if (args.documentNumber) formData.append("documentNumber", args.documentNumber);
    formData.append("file", args.file);

    const res = await fetch("/api/admin/employee-documents", {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to upload document.");

    await dispatch(fetchDocumentChecklist(args.employeeId));
    return true;
  }
);

export const reviewEmployeeDocument = createAsyncThunk(
  "onboarding/reviewDocument",
  async (
    args: {
      employeeId: string;
      documentId: number;
      status: "VERIFIED" | "REJECTED";
      remarks?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    const res = await fetch(`/api/admin/employee-documents/${args.documentId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: args.status, remarks: args.remarks || null }),
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to review document.");

    await dispatch(fetchDocumentChecklist(args.employeeId));
    return true;
  }
);

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    clearOnboardingResult(state) {
      state.lastOnboarded = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(onboardEmployee.pending, (state) => {
        state.submitting = true;
      })
      .addCase(onboardEmployee.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastOnboarded = action.payload;
      })
      .addCase(onboardEmployee.rejected, (state) => {
        state.submitting = false;
      })
      .addCase(linkExistingEmployee.pending, (state) => {
        state.submitting = true;
      })
      .addCase(linkExistingEmployee.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(linkExistingEmployee.rejected, (state) => {
        state.submitting = false;
      })
      .addCase(fetchOnboardingEmployee.pending, (state) => {
        state.employeeLoading = true;
        state.employee = null;
      })
      .addCase(fetchOnboardingEmployee.fulfilled, (state, action) => {
        state.employeeLoading = false;
        state.employee = action.payload;
      })
      .addCase(fetchOnboardingEmployee.rejected, (state) => {
        state.employeeLoading = false;
      })
      .addCase(fetchDocumentChecklist.pending, (state) => {
        state.checklistLoading = true;
      })
      .addCase(fetchDocumentChecklist.fulfilled, (state, action) => {
        state.checklistLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchDocumentChecklist.rejected, (state) => {
        state.checklistLoading = false;
      })
      .addCase(uploadEmployeeDocument.pending, (state, action) => {
        state.uploadingTypeId = action.meta.arg.documentTypeId;
      })
      .addCase(uploadEmployeeDocument.fulfilled, (state) => {
        state.uploadingTypeId = null;
      })
      .addCase(uploadEmployeeDocument.rejected, (state) => {
        state.uploadingTypeId = null;
      })
      .addCase(reviewEmployeeDocument.pending, (state, action) => {
        state.reviewingDocumentId = action.meta.arg.documentId;
      })
      .addCase(reviewEmployeeDocument.fulfilled, (state) => {
        state.reviewingDocumentId = null;
      })
      .addCase(reviewEmployeeDocument.rejected, (state) => {
        state.reviewingDocumentId = null;
      });
  },
});

export const { clearOnboardingResult } = onboardingSlice.actions;
export default onboardingSlice.reducer;
